import {
  CyclabilityZone,
  CyclabilityZoneStats,
  Period,
  TBackendAdministrativeLevel,
  getCyclabilityZonesPeriods,
} from '@geovelo-frontends/commons';
import { useQueries } from '@tanstack/react-query';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getChildrenBackendLevel } from '../utils/cyclability-zone';
import {
  TZoneStatsMap,
  fetchChildrenStats,
  fetchZoneStats,
  getDefaultStatsPeriod,
  getOlderStatsPeriod,
  mergeStats,
  statsKeys,
  sumStats,
} from '../utils/zone-stats';

interface IOlderPeriods {
  zoneId?: number;
  periods: Period[];
  pendingKey: string | null;
  selectPrevious: boolean;
  monthsCount: number;
}

const defaultOlderMonthsCount = 2;

interface IProps {
  currentZone?: CyclabilityZone | null;
  zoneMap: { [id: number]: CyclabilityZone };
  childrenMap: { [id: number]: number[] | null };
  periods?: Array<{ key: string; date: Moment }>;
  selectedKey: string | null;
  selectedIndex: number | null;
  setStatsPeriods: (periods?: Array<{ key: string; date: Moment }>) => void;
  selectStatsKey: (key: string | null) => void;
  selectStatsIndex: (index: number | null) => void;
}

const noPeriods: Period[] = [];

function useZoneStats({
  currentZone,
  zoneMap,
  childrenMap,
  periods,
  selectedKey,
  selectedIndex,
  setStatsPeriods,
  selectStatsKey,
  selectStatsIndex,
}: IProps) {
  const [older, setOlder] = useState<IOlderPeriods>({
    periods: noPeriods,
    pendingKey: null,
    selectPrevious: false,
    monthsCount: defaultOlderMonthsCount,
  });

  const zoneId = currentZone?.id;
  const isOlderOfCurrentZone = older.zoneId === zoneId;
  const olderPeriods = isOlderOfCurrentZone ? older.periods : noPeriods;
  const pendingKey = isOlderOfCurrentZone ? older.pendingKey : null;

  const ranges = [getDefaultStatsPeriod(), ...olderPeriods];
  const earliestRange = ranges[ranges.length - 1];
  const childrenIds = currentZone ? childrenMap[currentZone.id] : undefined;
  const childrenLevel = currentZone
    ? getChildrenBackendLevel(currentZone.administrativeLevel)
    : null;
  const isWorld = currentZone?.administrativeLevel === 'world';
  const ready = Boolean(currentZone) && childrenIds !== undefined;

  const zoneResults = useQueries({
    queries: ranges.map((range) => ({
      queryKey: statsKeys.zone(zoneId ?? -1, currentZone?.administrativeLevel ?? '', range),
      queryFn: () => fetchZoneStats(currentZone as CyclabilityZone, range),
      enabled: ready && !isWorld,
    })),
  });
  const childrenResults = useQueries({
    queries: ranges.map((range) => ({
      queryKey: statsKeys.children(zoneId ?? -1, childrenLevel ?? '', range),
      queryFn: () =>
        fetchChildrenStats(
          currentZone as CyclabilityZone,
          childrenLevel as TBackendAdministrativeLevel,
          range,
        ),
      enabled: ready && Boolean(childrenIds) && childrenLevel !== null,
    })),
  });

  const lastZoneResult = zoneResults[zoneResults.length - 1];
  const lastChildrenResult = childrenResults[childrenResults.length - 1];
  const defaultLoading = zoneResults[0].isLoading || childrenResults[0].isLoading;
  const loadingOlder =
    olderPeriods.length > 0 && (lastZoneResult.isLoading || lastChildrenResult.isLoading);

  const dataVersion = [...zoneResults, ...childrenResults]
    .map(({ dataUpdatedAt }) => dataUpdatedAt)
    .join(',');

  const byZone = useMemo<TZoneStatsMap>(() => {
    if (!currentZone) return {};

    const parts: { [id: number]: CyclabilityZoneStats[][] } = {};
    function add(id: number, stats?: CyclabilityZoneStats[]) {
      if (!stats) return;

      if (!parts[id]) parts[id] = [];
      parts[id].push(stats);
    }

    zoneResults.forEach(({ data }) => add(currentZone.id, data));
    childrenResults.forEach(({ data }) => {
      if (data) Object.entries(data).forEach(([id, stats]) => add(Number(id), stats));
    });

    const res: TZoneStatsMap = {};
    Object.entries(parts).forEach(([id, idParts]) => {
      res[Number(id)] = mergeStats(idParts);
    });

    if (isWorld) res[currentZone.id] = sumStats(res);

    return res;
  }, [currentZone, isWorld, dataVersion]);

  const canLoadOlder =
    ready &&
    !loadingOlder &&
    Boolean(periods?.length) &&
    getOlderStatsPeriod(earliestRange, defaultOlderMonthsCount) !== null;

  function searchOlder(
    zone: CyclabilityZone,
    key: string,
    selectPrevious: boolean,
    monthsCount: number,
  ): IOlderPeriods | null {
    const period = getOlderStatsPeriod(earliestRange, monthsCount);

    return period
      ? {
          zoneId: zone.id,
          periods: [...olderPeriods, period],
          pendingKey: key,
          selectPrevious,
          monthsCount,
        }
      : null;
  }

  useEffect(() => {
    if (currentZone === null) {
      setStatsPeriods(undefined);
      selectStatsKey(null);
      selectStatsIndex(null);

      return;
    }

    if (!currentZone || !ready || defaultLoading || loadingOlder) return;

    const zones = [currentZone.id, ...(childrenIds || []).filter((id) => zoneMap[id])].map(
      (id) => ({ id, stats: byZone[id] || [] }),
    );
    const nextPeriods = getCyclabilityZonesPeriods(zones);

    setStatsPeriods(nextPeriods);

    if (pendingKey) {
      const pendingIndex = nextPeriods.findIndex(({ key }) => key === pendingKey);

      if (pendingIndex > 0) {
        setOlder({ ...older, pendingKey: null, monthsCount: defaultOlderMonthsCount });

        if (older.selectPrevious) {
          selectStatsKey(nextPeriods[pendingIndex - 1].key);
          selectStatsIndex(pendingIndex - 1);

          return;
        }
      } else {
        const widerSearch = searchOlder(
          currentZone,
          pendingKey,
          older.selectPrevious,
          older.monthsCount * 2,
        );

        setOlder(widerSearch || { ...older, pendingKey: null });

        if (widerSearch) return;
      }
    }

    const index = nextPeriods.findIndex(({ key }) => key === selectedKey);
    const nextIndex =
      index === -1 ? (nextPeriods.length > 0 ? nextPeriods.length - 1 : null) : index;

    selectStatsIndex(nextIndex);
    if (nextIndex === null) return;

    selectStatsKey(nextPeriods[nextIndex].key);

    if (nextIndex === 0) {
      const search = searchOlder(currentZone, nextPeriods[0].key, false, defaultOlderMonthsCount);
      if (search) setOlder(search);
    }
  }, [currentZone, childrenMap, byZone, defaultLoading, loadingOlder, pendingKey]);

  useEffect(() => {
    if (selectedIndex !== null) {
      selectStatsKey(periods?.[selectedIndex]?.key || null);
    }
  }, [selectedIndex]);

  const loadOlder = useCallback(() => {
    if (!currentZone || !canLoadOlder || !periods?.length) return;

    const search = searchOlder(currentZone, periods[0].key, true, defaultOlderMonthsCount);
    if (search) setOlder(search);
  }, [currentZone, canLoadOlder, periods, olderPeriods]);

  return { byZone, canLoadOlder, loadingOlder, loadOlder };
}

export default useZoneStats;
